/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-02 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Popover, Button, ActionList } from '@shopify/polaris';
import { HorizontalDotsMinor, CancelSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import DismissNotice from './dismissNotice';
import Feedback from './feedback';
import AfterFeedback from './afterFeedback';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import * as dashboardAction from 'actions/dashboard-actions';

import type { DashboardAction } from 'stores/flow-type/dashboard.type';

type Props = {
  loading: boolean,
  blockName: string,
  processDismissBlock: (blockName: string) => DashboardAction,
  undoDismissBlock: (blockName: string) => DashboardAction
};

type State = {
  itemShow: string,
  active: boolean
};

type ComposedProps = Props & WithI18nProps;

function withBlockCanDismiss(WrappedComponent: Object) {
  class HOCBlockCanDismiss extends Component<ComposedProps, State> {
    state = {
      // Define item which is show in current: main, dismiss, feedback, after-feedback
      itemShow: 'main',
      active: false
    };

    componentWillReceiveProps(nextProps) {
      if (nextProps.loading === true && this.props.loading === false) {
        startSABLoading();
      }

      if (nextProps.loading === false && this.props.loading === true) {
        stopSABLoading();
      }
    }

    render() {
      const { itemShow } = this.state;
      const { blockName, i18n } = this.props;

      const renderPopover = () => (
        <Popover
          active={this.state.active}
          activator={<Button icon={HorizontalDotsMinor} plain onClick={this.togglePopover} />}
          onClose={this.togglePopover}
          preferredAlignment="right">
          <ActionList
            items={[
              {
                content: i18n.translate('AdminHTML.Common.Action.dismiss'),
                icon: CancelSmallMinor,
                onAction: () => this.processDismiss()
              }
            ]}
          />
        </Popover>
      );

      return (
        <React.Fragment>
          <SwitchTransition>
            <CSSTransition key={itemShow} timeout={500} classNames="BC-Transition">
              <div id={`BC-${blockName}__Wrapper`}>
                {(() => {
                  switch (itemShow) {
                    case 'main':
                      return <WrappedComponent {...this.props} renderPopover={renderPopover} />;
                    case 'dismiss':
                      return (
                        <DismissNotice
                          onCloseDismissNotice={this.onCloseDismissNotice}
                          undoDismiss={this.undoDismiss}
                          changeItemShow={this.changeItemShow}
                        />
                      );
                    case 'feedback':
                      return (
                        <Feedback changeItemShow={this.changeItemShow} blockName={blockName} />
                      );
                    case 'after-feedback':
                      return <AfterFeedback onCloseDismissNotice={this.onCloseDismissNotice} />;

                    default:
                      return '';
                  }
                })()}
              </div>
            </CSSTransition>
          </SwitchTransition>
        </React.Fragment>
      );
    }

    // Change the item to display
    changeItemShow = (itemShow: string) => {
      this.setState({ itemShow });
    };

    // Toggle state to show/hide the popover
    togglePopover = () => {
      this.setState(({ active }) => {
        return { active: !active };
      });
    };

    // Call API to dismiss block
    processDismiss = async () => {
      const { blockName, processDismissBlock } = this.props;
      const result = await processDismissBlock(blockName);

      if (result.type === 'PROCESS_DISMISS_BLOCK_SUCCESS') {
        this.changeItemShow('dismiss');
        this.togglePopover();
      }
    };

    // Call API to undo dismiss block
    undoDismiss = async () => {
      const { blockName, undoDismissBlock } = this.props;
      const result = await undoDismissBlock(blockName);

      if (result.type === 'UNDO_DISMISS_BLOCK_SUCCESS') {
        this.changeItemShow('main');
      }
    };

    // Close the dismiss notice block
    onCloseDismissNotice = () => {
      const { blockName } = this.props;
      this.changeItemShow('');

      const element = document.getElementById(`BC-${blockName}__Wrapper`);
      const elementParent = element ? element.parentElement : null;
      if (elementParent) {
        elementParent.className += 'empty';
      }
    };
  }

  const mapStateToProps = (state, props) => ({
    loading: _.indexOf(state.dashboard.dismissLoading, props.blockName) !== -1
  });

  const mapDispatchToProps = (dispatch, ownProps) => ({
    processDismissBlock: (blockName: string) =>
      dispatch(dashboardAction.processDismissBlock(blockName, ownProps.i18n)),
    undoDismissBlock: (blockName: string) =>
      dispatch(dashboardAction.undoDismissBlock(blockName, ownProps.i18n))
    // confirmDismissBlock: (blockName: string) =>
    //   dispatch(dashboardAction.confirmDismissBlock(blockName))
  });

  return withI18n()(connect(mapStateToProps, mapDispatchToProps)(HOCBlockCanDismiss));
}

export default withBlockCanDismiss;
